import MaturaCurriculumNotice from "../../components/MaturaCurriculumNotice";
import { ABSOLUTE_INEQUALITY_RULE } from "../maturaCurriculumScope";
import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import TheoryExamplesRunner from "../../components/TheoryExamplesRunner";

const examples = [
  {
    label: "Przykład 1. Wartość bezwzględna z pierwiastkiem",
    summary: "|√23 − 5|",
    content: (
      <>
        <p>
          Najpierw porównujemy pierwiastek z najbliższymi liczbami całkowitymi:
        </p>
        <BlockMath math={String.raw`4<\sqrt{23}<5.`} />
        <p>
          Zatem <InlineMath math={String.raw`\sqrt{23}-5<0`} />. Zmieniamy
          znak wyrażenia znajdującego się pod wartością bezwzględną:
        </p>
        <BlockMath math={String.raw`|\sqrt{23}-5|=5-\sqrt{23}.`} />
      </>
    ),
  },
  {
    label: "Przykład 2. Działanie na wartościach bezwzględnych",
    summary: "|√23 − 4| − |√23 − 5|",
    content: (
      <>
        <p>
          Ponieważ <InlineMath math={String.raw`4<\sqrt{23}<5`} />, pierwsze
          wyrażenie pod kreskami jest dodatnie, a drugie ujemne:
        </p>
        <BlockMath
          math={String.raw`
            |\sqrt{23}-4|-|\sqrt{23}-5|
            =(\sqrt{23}-4)-(5-\sqrt{23})
            =2\sqrt{23}-9.
          `}
        />
      </>
    ),
  },
  {
    label: "Przykład 3. Proste równanie",
    summary: "|x - 3| = 5",
    content: (
      <>
        <p>
          Liczba <InlineMath math="x" /> może być oddalona od{" "}
          <InlineMath math="3" /> o <InlineMath math="5" /> w dwóch
          kierunkach:
        </p>
        <BlockMath
          math={String.raw`|x-3|=5\quad\Longleftrightarrow\quad x-3=5\ \text{lub}\ x-3=-5.`}
        />
        <BlockMath math={String.raw`x=8\ \text{lub}\ x=-2.`} />
      </>
    ),
  },
  {
    label: "Przykład 4. Równanie ze współczynnikami",
    summary: "3|2x + 1| = 21",
    content: (
      <>
        <p>
          Najpierw dzielimy obie strony równania przez współczynnik stojący
          przed wartością bezwzględną:
        </p>
        <BlockMath math={String.raw`3|2x+1|=21\quad\Longleftrightarrow\quad |2x+1|=7.`} />
        <p>Następnie rozpatrujemy dwa przypadki:</p>
        <BlockMath
          math={String.raw`|2x+1|=7\quad\Longleftrightarrow\quad 2x+1=7\ \text{lub}\ 2x+1=-7.`}
        />
        <BlockMath
          math={String.raw`2x=6\ \text{lub}\ 2x=-8.`}
        />
        <BlockMath math={String.raw`x=3\ \text{lub}\ x=-4.`} />
      </>
    ),
  },
];

const AbsoluteValuePreparationTheory = () => (
  <>

  <TheoryExamplesRunner
    title="Wartość bezwzględna"
    intro={
      <>
        <p>
          Wartość bezwzględna liczby jest jej odległością od zera na osi
          liczbowej. Odległość nie może być ujemna.
        </p>
        <BlockMath
          math={String.raw`|x|=\begin{cases}x&\text{dla }x\geq 0,\\-x&\text{dla }x<0.\end{cases}`}
        />
        <p>
          Dlatego <InlineMath math={String.raw`|-4|=4`} /> oraz{" "}
          <InlineMath math={String.raw`|6|=6`} />.
        </p>
        <p>
          Jeżeli <InlineMath math={String.raw`c\geq 0`} />, to równanie z wartością
          bezwzględną rozpisujemy na dwa przypadki:
        </p>
        <BlockMath
          math={String.raw`|A|=c\quad\Longleftrightarrow\quad A=c\ \text{lub}\ A=-c.`}
        />
        <p>
          Poniższe przykłady przygotowują do działań z pierwiastkami oraz
          rozwiązywania równań.
        </p>
      </>
    }
    examples={examples}
  />
  <details style={{marginTop:24}}>
    <summary style={{cursor:"pointer",fontWeight:700}}>Dodatkowo: nierówności z wartością bezwzględną — zakres archiwalny</summary>
    <MaturaCurriculumNotice rule={ABSOLUTE_INEQUALITY_RULE} compact/>
    <p>Ta część jest dobrowolnym przygotowaniem do starszych zadań. Nie należy do domyślnych ćwiczeń i quizu ścieżki.</p>
    <p>Wartość bezwzględna opisuje odległość. Dla dodatniego promienia:</p>
    <BlockMath math={String.raw`r>0:\quad |x-a|<r\iff a-r<x<a+r`} />
    <BlockMath math={String.raw`|x-a|>r\iff x<a-r\ \text{lub}\ x>a+r`} />
    <p>Przy znakach nierówności z równością dołączamy końce przedziałów. Najpierw izolujemy moduł; powyższe reguły zakładają dodatnią prawą stronę.</p>
    <p>Przykład: odległość od 3 ma być mniejsza niż 2.</p>
    <BlockMath math={String.raw`|x-3|<2\iff 1<x<5,\qquad x\in(1;5)`} />
    <p>Przykład z dwoma przedziałami i końcami należącymi do rozwiązania:</p>
    <BlockMath math={String.raw`|x+1|\ge3\iff x\le-4\ \text{lub}\ x\ge2`} />
    <BlockMath math={String.raw`x\in(-\infty;-4]\cup[2;+\infty)`} />
    <p>Przykład ze współczynnikiem przy niewiadomej:</p>
    <BlockMath math={String.raw`|2x-1|\le3\iff -3\le2x-1\le3\iff -1\le x\le2`} />
    <p>Moduł nie jest ujemny: nierówność z modułem mniejszym od liczby ujemnej nie ma rozwiązań. Przy prawej stronie równej zeru rozpatrujemy osobno znak i możliwość równości.</p>
    <a href="/matura/sciezka/zadania/1.1.2?mode=practice&archive=1">Przejdź do dodatkowych archiwalnych zadań maturalnych</a>
  </details>
  </>
);

export default AbsoluteValuePreparationTheory;
